import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowBigUp,
  ArrowRight,
  Award,
  Baby,
  BedDouble,
  ChevronRight,
  Clock,
  GitCompare,
  GraduationCap,
  Heart,
  Layers,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Sparkles,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/breast-fat-transfer-lipofilling-mumbai";

const BREAST_FAT_TRANSFER_FAQS = [
  {
    question: "How much size increase does breast fat transfer produce?",
    answer:
      "Typically half to one cup size per session. This is the realistic range for a single fat transfer procedure. Patients who want more than one cup size increase either need multiple sessions spaced 3 to 6 months apart to allow the first session's fat to stabilize, or are better served by implants depending on their anatomy and goals.",
  },
  {
    question: "What percentage of transferred fat survives?",
    answer:
      "Approximately 60 to 80 percent of transferred fat cells survive and become permanently integrated. The remaining 20 to 40 percent is naturally resorbed by the body over the first 3 months. The injection volume at surgery is planned to account for this resorption so the final result reflects the intended outcome.",
  },
  {
    question: "Are the results from breast fat transfer permanent?",
    answer:
      "The fat cells that survive the transfer are permanently integrated into the breast tissue. However, they behave like fat anywhere else in the body: they respond to significant weight changes. Substantial weight loss after transfer can reduce the transferred volume alongside fat elsewhere. Substantial weight gain can increase it. Patients at a stable weight maintain the most consistent long-term result.",
  },
  {
    question: "Is breast fat transfer safer than implants?",
    answer:
      "Both are safe procedures with different risk profiles. Fat transfer carries no implant-specific risks: no capsular contracture, no implant rupture, and no displacement. It does carry fat-specific risks: resorption, and in a small percentage of cases, fat necrosis where fat cells that do not receive blood supply form small firm lumps that may require treatment. Neither procedure is universally safer. The appropriate choice depends on the patient's anatomy, goals, and how the specific risk profiles of each option align with their priorities.",
  },
  {
    question: "Can fat transfer be combined with breast implants?",
    answer:
      "Yes. Fat grafting is frequently used alongside implants to improve soft tissue coverage over implant edges, smooth contour irregularities, and add natural-feeling tissue over areas where the implant is visible or palpable. The two techniques complement each other in ways that neither achieves alone.",
  },
  {
    question: "How many sessions of fat transfer are needed?",
    answer:
      "One session is sufficient for most patients seeking modest enhancement or asymmetry correction. Patients who want more than one cup size increase may require two sessions spaced 3 to 6 months apart to allow the first session's result to stabilize before adding more volume. The number of sessions also depends on how much donor fat is available in a single harvest.",
  },
  {
    question: "Will there be visible scarring after breast lipofilling?",
    answer:
      "The liposuction access points are small, typically 3 to 4 millimeters, and placed in concealed locations on the donor area. The breast itself requires no incisions as fat is injected through fine cannulas. Scarring from this procedure is minimal and the access points are essentially invisible once healed.",
  },
  {
    question: "Can breast lipofilling affect mammograms?",
    answer:
      "Fat grafting can occasionally create calcifications or areas of fat necrosis visible on mammography that require differentiation from other breast changes. Patients should inform their radiologist that they have had fat grafting to the breasts when undergoing mammography so the images are interpreted in the appropriate context. This is a documentation and communication issue, not a reason to avoid the procedure.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Breast fat transfer (breast lipofilling)",
      alternateName: ["Breast lipofilling", "Autologous fat grafting to the breast", "Fat transfer breast augmentation"],
      description:
        "Surgical enhancement of breast volume and contour using the patient's own fat harvested by liposuction, processed, and injected in layered passes; modest cup increase per session; mammography after grafting may show changes—patients should inform radiologists of prior fat grafting for correct image interpretation.",
      procedureType: "Surgical",
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Breast" },
      howPerformed:
        "General anesthesia or sedation; liposuction of donor sites; fat processing (centrifugation or filtration); layered micro-injection into breast planes; compression garment on donor area; 2–4 hours; same-day or overnight stay.",
      url: PAGE_URL,
      additionalProperty: [
        { "@type": "PropertyValue", name: "Procedure category", value: "Surgical" },
        {
          "@type": "PropertyValue",
          name: "Screening note",
          value:
            "Inform mammography providers of breast fat grafting history so calcifications or graft-related findings are interpreted in context.",
        },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: BREAST_FAT_TRANSFER_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function BreastFatTransferJsonLd() {
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
  );
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Breast", href: "/#services" },
    { name: "Breast Fat Transfer", href: "/breast-fat-transfer-lipofilling-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Breast</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-5">
              Breast Fat Transfer
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg text-deep-navy/70 mb-8">
              Natural Breast Enhancement with Fat Grafting by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Breast Fat Transfer" icon="🌸" size="hero"  imageSrc={HERO.breastFatTransfer} imageAlt="Breast fat transfer"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What is Breast Lipofilling?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Breast lipofilling, also called fat transfer to the breasts or autologous fat grafting, enhances breast volume and
              contour using the patient&apos;s own body fat rather than implants. Fat is harvested from a donor area, typically the
              abdomen, thighs, or flanks, through liposuction, processed to isolate healthy fat cells, and injected in small
              precise amounts into the breast tissue to increase volume and improve shape.
            </p>
            <p>
              The procedure produces a natural result because the material being added is the patient&apos;s own tissue. There is no
              foreign body, no implant shell, and no risk of implant-related complications. The breast feels soft and behaves like
              natural breast tissue because it is. The trade-off relative to implants is the degree of size increase achievable: fat
              transfer produces a half to one cup size increase per session, not the two to three cup size change that implants can
              produce.
            </p>
            <p>
              Dr. Vicky Jain performs breast lipofilling for patients who want modest, natural-feeling breast enhancement,
              correction of asymmetry, volume restoration after pregnancy or weight loss, or contour refinement alongside or after
              implant surgery. The procedure has a dual benefit: the donor area is contoured by the liposuction component at the
              same time the breasts are enhanced.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Breast Lipofilling</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Fat grafting to the breast requires precision at every step: gentle liposuction technique to harvest fat without
                damaging the cells, careful purification to separate viable fat from blood and fluid, and injection in thin passes
                across multiple tissue planes within the breast to maximise the surface area over which the fat is distributed. Fat
                deposited in large clumps rather than thin passes has a higher resorption rate and a higher risk of fat necrosis,
                small firm lumps that develop when fat cells do not receive adequate blood supply.
              </p>
              <p>
                Dr. Jain plans the donor site and the volume to be transferred together. The donor area must have adequate fat for
                the planned enhancement. Very thin patients with minimal fat stores are not appropriate candidates because there is
                insufficient donor material to achieve a meaningful breast volume change. The planned transfer volume also accounts
                for the 60 to 80 percent fat survival rate, meaning the injected volume at surgery is calculated to produce the
                intended result after the natural resorption that occurs during the first 3 months of healing.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatAddressesSection() {
  const cards = [
    {
      title: "Modest Breast Enlargement Without Implants",
      icon: ArrowBigUp,
      copy: "Patients who want a half to one cup size increase and prefer their own tissue over a synthetic implant are the primary candidates for fat transfer. The result is softer and more natural in feel and movement than implants at this size range.",
    },
    {
      title: "Breast Asymmetry",
      icon: GitCompare,
      copy: "Volume differences between breasts, whether from development, surgery, or weight change, can be corrected by transferring fat selectively to the smaller side. Fat grafting allows precise volume addition in specific areas that implants cannot target with the same granularity.",
    },
    {
      title: "Post-Pregnancy Volume Loss",
      icon: Baby,
      copy: "Breastfeeding frequently reduces breast volume below its pre-pregnancy level. Fat transfer restores this volume using fat from areas that may have increased during pregnancy, making this a practical option for post-pregnancy breast restoration.",
    },
    {
      title: "Contour Defects After Implants",
      icon: Layers,
      copy: "Patients with implants who have rippling, visible implant edges, or areas of irregular contour can have fat grafted over these areas to improve the coverage and smoothness of the result without removing or replacing the implant.",
    },
    {
      title: "Post-Surgical Breast Reconstruction",
      icon: Heart,
      copy: "Fat grafting is used in breast reconstruction to refine volume, improve contour, and improve the quality of the overlying skin and tissue after mastectomy and reconstruction. Multiple sessions are often used progressively to build the desired result.",
    },
    {
      title: "Soft Tissue Coverage Improvement",
      icon: Sparkles,
      copy: "In patients with thin breast tissue where implant edges or reconstruction boundaries are visible, fat grafting adds soft tissue coverage over these areas, improving the natural appearance of the transition zone.",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-addresses-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">What Breast Fat Transfer Addresses</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const VS_IMPLANT_ROWS = [
  {
    label: "Material",
    fat: "Patient's own fat cells. No foreign material.",
    implant: "Silicone gel or saline in a synthetic shell.",
  },
  {
    label: "Feel and Movement",
    fat: "Soft, natural. Behaves identically to native breast tissue.",
    implant: "Very natural with modern cohesive silicone, though slightly different from native tissue.",
  },
  {
    label: "Size Increase Possible",
    fat: "Half to one cup size per session. Multiple sessions needed for more.",
    implant: "One to three cup size increase in a single procedure.",
  },
  {
    label: "Scarring",
    fat: "Tiny liposuction access points, practically invisible.",
    implant: "Small incisions at inframammary fold, areola, or axilla, well-concealed but present.",
  },
  {
    label: "Implant-Related Risks",
    fat: "None. No capsular contracture, no implant rupture, no displacement.",
    implant: "Small risk of capsular contracture, implant rupture, or positional change over time.",
  },
  {
    label: "Fat Resorption",
    fat: "20 to 40 percent of transferred fat resorbs during healing. Surviving fat is permanent.",
    implant: "No equivalent. Volume is stable from surgery.",
  },
  {
    label: "Best For",
    fat: "Patients wanting modest, natural enhancement, asymmetry correction, or implant-free augmentation with adequate donor fat.",
    implant: "Patients wanting significant size increase or who lack sufficient donor fat for transfer.",
  },
];

function FatTransferVsImplantsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="fat-transfer-vs-implants-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          Breast Fat Transfer vs. Breast Implants
        </h2>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[22%]" />
                <th scope="col" className="py-4 px-3 font-serif text-premium-purple bg-white/80">
                  Fat transfer
                </th>
                <th scope="col" className="py-4 pl-3 font-serif text-premium-purple bg-white/80">
                  Implants
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_IMPLANT_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-4 px-3 text-deep-navy/70 align-top">{row.fat}</td>
                  <td className="py-4 pl-3 text-deep-navy/70 align-top">{row.implant}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center leading-relaxed">
          Neither option is universally superior. The right choice depends on how much size increase is wanted, whether the
          patient has adequate donor fat, and whether a foreign material is acceptable. Dr. Jain discusses both options during
          consultation so patients make an informed decision based on their specific anatomy and goals.
        </p>
      </div>
    </section>
  );
}

function HowSurgeryPerformedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">How Breast Fat Transfer is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 2 to 4 hours under general anesthesia or sedation depending on the volume of fat being transferred and
              the number of donor areas involved. Dr. Jain first performs liposuction on the planned donor areas using small access
              points that leave practically invisible marks. The harvested fat is collected in a closed system that minimises
              exposure and cell damage.
            </p>
            <p>
              The fat is then processed by centrifugation or filtration to separate viable fat cells from blood, oil, and fluid.
              Only the healthy, intact fat cells are used for injection. This purification step directly affects how much fat
              survives after transfer.
            </p>
            <p>
              The purified fat is injected into the breast in multiple thin passes using fine cannulas across different tissue planes:
              deep to the glandular tissue, within the glandular tissue, and in the subcutaneous layer. Distributing the fat in thin
              passes across multiple planes maximises contact with the surrounding vasculature, which is what allows the fat cells
              to receive blood supply and survive. Dr. Jain assesses symmetry and volume distribution at each stage before closure.
            </p>
            <p>
              A compression garment is applied to the donor area immediately after surgery. The breasts do not require compression.
              Most patients go home the same day or after one night.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 5",
      description:
        "Swelling and bruising at the liposuction donor sites. Mild swelling in the breasts. The donor area is more uncomfortable than the breasts. Compression garment worn continuously on the donor area.",
      icon: BedDouble,
    },
    {
      period: "Week 1 to 2",
      description:
        "Light daily activity resumes. Donor site soreness reduces. The breasts look fuller than the final result due to procedural swelling, which is normal.",
      icon: Monitor,
    },
    {
      period: "Weeks 2 to 3",
      description:
        "Most patients return to desk work and normal routine. Donor area compression garment continues. Avoid pressure on the breasts.",
      icon: Clock,
    },
    {
      period: "Month 3",
      description:
        "Fat resorption is complete. The surviving fat cells are permanently integrated. Final breast volume and shape are assessed at this visit. If a second session is being considered, it is planned from this baseline.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">
            Who Is a Good Candidate for Breast Lipofilling?
          </h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Good candidates are women who want a modest, natural-feeling breast enhancement of half to one cup size, have
              adequate donor fat in the abdomen, thighs, or flanks, and prefer their own tissue over a synthetic implant. Patients who
              want simultaneous body contouring alongside breast enhancement get a dual benefit from the liposuction component.
              Patients correcting asymmetry or improving contour after prior breast surgery are also well-suited to fat grafting.
            </p>
            <p>
              Patients who are very thin with minimal donor fat are not appropriate candidates because there is insufficient material
              for a meaningful transfer. Patients who want a two to three cup size increase in a single procedure are better served
              by implants, as fat transfer cannot reliably achieve this in one session. Patients at a stable weight get the most
              predictable results. Significant weight loss after fat transfer reduces the transferred fat alongside fat elsewhere in
              the body, potentially reversing part of the volume gain. Weight gain after transfer can increase the transferred fat
              volume alongside the rest of the body, potentially producing more enhancement than was originally intended.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Breast" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {BREAST_FAT_TRANSFER_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`bft-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">
          Book Your Breast Lipofilling Consultation
        </h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain evaluates your breast volume, donor fat availability, and enhancement goals to determine
          whether fat transfer is the right technique for your anatomy and what result is achievable in a single session. If implants
          or a combined approach would better serve your goals, that recommendation is made directly. You leave with a clear plan
          based on what your body specifically allows.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Breast Augmentation",
      description: "Implant-based enhancement for patients wanting significant size increase beyond what fat transfer achieves.",
      href: "/breast-implant-surgery-mumbai",
    },
    {
      name: "Breast Lift",
      description: "Address breast position and ptosis alongside volume restoration for a complete result.",
      href: "/breast-lift-surgery-mumbai",
    },
    {
      name: "Liposuction",
      description: "Targeted fat removal from donor areas, already part of the lipofilling procedure.",
      href: "/liposuction-surgery-mumbai",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function BreastFatTransferPage() {
  return (
    <>
      <SEO
        title="Breast Fat Transfer | Lipofilling by Dr. Vicky Jain"
        description="Natural breast enhancement using your own fat. Dr. Vicky Jain performs breast lipofilling for subtle, implant-free augmentation. Book your consultation."
        canonical="/breast-fat-transfer-lipofilling-mumbai"
      />
      <BreastFatTransferJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <WhatAddressesSection />
      <FatTransferVsImplantsSection />
      <HowSurgeryPerformedSection />
      <RecoveryTimelineSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
